import React from 'react';
import PropTypes from 'prop-types';

import { Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faInfoCircle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  white,
  squidInk,
  coolGrey,
  grape,
  greyStroke,
  shadowLight,
  shadowMedium,
} from 'lib/css/colors';
import { getWidgetConfig } from 'lib/configs/dashboardWidgetConfigs';
import { DashboardWidgetTypeTooltips } from 'lib/enums/DashboardWidgetTypes';
import PercentageChange from 'components/dashboard/PercentageChange';

function renderOtherCounts(otherCounts) {
  if (!otherCounts) return null;

  const counts = [];

  if (otherCounts.ai_verified_count) {
    counts.push(`AI Verified: ${otherCounts.ai_verified_count}`);
  }

  if (counts.length === 0) return null;

  return <span className={css(styles.otherCounts)}>({counts.join('; ')})</span>;
}

function renderValue(totalCount, specificCount, otherCounts) {
  if (specificCount !== undefined) {
    const specificPercentage =
      totalCount > 0 ? ((100 * specificCount) / totalCount).toFixed(2) : '0.00';
    return (
      <div className={css(styles.valueContainer)}>
        <span className={css(styles.mainValue)}>{specificPercentage}%</span>
        <span className={css(styles.subValue)}>
          ({specificCount.toLocaleString()}/{totalCount.toLocaleString()})
        </span>
        {renderOtherCounts(otherCounts)}
      </div>
    );
  } else {
    return (
      <div className={css(styles.valueContainer)}>
        <span className={css(styles.mainValue)}>{totalCount.toLocaleString()}</span>
        {renderOtherCounts(otherCounts)}
      </div>
    );
  }
}

function DashboardWidget({
  label,
  loading,
  percentageChange,
  dateDifference,
  onClick,
  totalCount,
  specificCount,
  otherCounts,
  widgetType,
}) {
  const tooltipText = DashboardWidgetTypeTooltips[widgetType];
  const config = getWidgetConfig(widgetType);

  return (
    <div className={css(styles.container)}>
      {/* Accent bar at top */}
      <div
        className={css(styles.accentBar)}
        style={{
          background: `linear-gradient(90deg, ${config.gradient[0]}, ${config.gradient[1]})`,
        }}
      />

      <div className={css(styles.content)}>
        {/* Header with icon and title */}
        <div className={css(styles.header)}>
          <div className={css(styles.iconContainer)} style={{ backgroundColor: config.iconBg }}>
            <FontAwesomeIcon
              icon={config.icon}
              className={css(styles.icon)}
              style={{ color: config.iconColor }}
            />
          </div>
          <div className={css(styles.titleContainer)}>
            <div className={css(styles.titleRow)}>
              <span className={css(styles.title)}>{label}</span>
              {tooltipText && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={`tooltip-${widgetType}`}>{tooltipText}</Tooltip>}
                >
                  <span className={css(styles.tooltipIcon)}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </div>

        {/* Value section */}
        <div className={css(styles.valueSection)}>
          {loading ? (
            <div className={css(styles.loadingContainer)}>
              <Spinner animation="border" role="status" size="sm" />
            </div>
          ) : (
            renderValue(totalCount, specificCount, otherCounts)
          )}
        </div>

        {/* Footer with percentage change and action */}
        {!loading && (
          <div className={css(styles.footer)}>
            <PercentageChange percentageChange={percentageChange} dateDifference={dateDifference} />
            {totalCount > 0 && onClick && (
              <Button variant="link" onClick={onClick} className={css(styles.viewButton)}>
                View
                <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.viewIcon)} />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    boxShadow: `0 1px 3px ${shadowLight}, 0 1px 2px ${shadowLight}`,
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    width: '100%',
    ':hover': {
      boxShadow: `0 4px 12px ${shadowMedium}`,
      transform: 'translateY(-2px)',
    },
  },
  accentBar: {
    height: 4,
    width: '100%',
  },
  content: {
    padding: '16px 20px 14px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: 18,
  },
  titleContainer: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: squidInk,
    lineHeight: 1.4,
  },
  tooltipIcon: {
    color: coolGrey,
    opacity: 0.6,
    cursor: 'pointer',
    fontSize: 12,
    ':hover': {
      opacity: 1,
    },
  },
  valueSection: {
    marginBottom: 12,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 36,
  },
  valueContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  mainValue: {
    fontSize: 28,
    fontWeight: 700,
    color: squidInk,
    lineHeight: 1.2,
  },
  subValue: {
    fontSize: 15,
    color: squidInk,
    fontWeight: 500,
    opacity: 0.65,
  },
  otherCounts: {
    fontSize: 12,
    color: coolGrey,
    marginTop: 4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 12,
    borderTop: `1px solid ${greyStroke}`,
  },
  viewButton: {
    color: grape,
    fontSize: 13,
    fontWeight: 600,
    padding: '4px 8px',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 6,
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: `${grape}15`,
      textDecoration: 'none',
    },
  },
  viewIcon: {
    fontSize: 10,
  },
});

DashboardWidget.propTypes = {
  label: PropTypes.string,
  percentageChange: PropTypes.number,
  loading: PropTypes.bool,
  dateDifference: PropTypes.number,
  onClick: PropTypes.func,
  totalCount: PropTypes.number,
  specificCount: PropTypes.number,
  otherCounts: PropTypes.object,
  widgetType: PropTypes.string,
};

export default DashboardWidget;
